import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { ApiServiceService } from './api-service.service';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshState$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.shouldAttemptRefresh(request, error)) {
          return throwError(() => error);
        }

        return this.handle401(request, next);
      }),
    );
  }

  private shouldAttemptRefresh(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ): boolean {
    if (error.status !== 401) {
      return false;
    }

    return !this.isAuthEndpoint(request.url);
  }

  private isAuthEndpoint(url: string): boolean {
    return ['/login', '/refresh', '/logout'].some((path) => url.includes(path));
  }

  private handle401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshState$.pipe(
        filter((ready) => ready),
        take(1),
        switchMap(() => next.handle(request)),
      );
    }

    this.isRefreshing = true;
    this.refreshState$.next(false);

    return this.apiService.refreshToken().pipe(
      switchMap(() => {
        this.refreshState$.next(true);
        return next.handle(request);
      }),
      catchError((refreshError) => {
        this.authenticationService.clearAuthenticatedSession();
        void this.router.navigate(['/login']);
        return throwError(() => refreshError);
      }),
      finalize(() => {
        this.isRefreshing = false;
      }),
    );
  }
}
